import Flickity from 'flickity';
import EventHandler from '@pixelunion/events';
import themeUtils from '../themeUtils';
import MasonryGrid from './MasonryGrid';

export default class ProductMasonry {
  constructor({ el }) {
    this.el = el;
    this.selectors = {
      gallery: {
        el: '[data-masonry-gallery]',
        itemSelector: '.product-gallery--viewport--figure',
        columnWidth: '[data-masonry-image-sizer]',
      },
      relatedProducts: {
        el: '[data-masonry-products]',
        itemSelector: '.product-list-item',
        columnWidth: '[data-masonry-products-sizer]',
      },
    };

    this.masonry = {
      gallery: null,
      relatedProducts: null,
    };

    this.flickity = {
      gallery: null,
      relatedProducts: null,
    };

    this.events = new EventHandler();

    this.gallery = this.el.querySelector(this.selectors.gallery.el);
    this.relatedProducts = this.el.querySelector(this.selectors.relatedProducts.el);

    this.hasGallery = !!this.gallery;
    this.hasRelatedProducts = !!this.relatedProducts;

    this._initLayout = this._initLayout.bind(this);

    this._bindEvents();
    this._initLayout();
  }

  remove() {
    this.events.unregisterAll();

    if (this.flickity) {
      this._destroyFlickity();
    }

    if (this.masonry) {
      this._destroyMasonry();
    }
  }

  onSectionUnload() {
    this.remove();
  }

  _bindEvents() {
    this.onRimgLoadEvent = () => {
      if (this.flickity.gallery) {
        themeUtils.flickityResize(this.flickity.gallery);
      }

      if (this.flickity.relatedProducts) {
        themeUtils.flickityResize(this.flickity.relatedProducts);
      }
    };
    this.resizeDebouncedEvent = themeUtils.debounce(this._initLayout, 100);
    this.events.register(window, 'resize', this.resizeDebouncedEvent);
    this.events.register(this.el, 'rimg:load', this.onRimgLoadEvent);
  }

  _initLayout() {
    if (themeUtils.isSmall()) {
      this._initMobile();
    } else {
      this._initDesktop();
    }
  }

  _initMobile() {
    this._destroyMasonry();
    this._initFlickity();
  }

  _initDesktop() {
    this._destroyFlickity();
    this._initMasonry();
  }

  _initFlickity() {
    if (this.hasGallery && !this.flickity.gallery) {
      this._galleryReset();

      this.flickity.gallery = new Flickity(this.gallery, {
        cellAlign: 'left',
        cellSelector: this.selectors.gallery.itemSelector,
        contain: false,
        percentPosition: false,
        prevNextButtons: true,
        adaptiveHeight: true,
        pageDots: true,
        setGallerySize: false,
        lazyLoad: false,
      });

      this._flickityEvents({
        el: this.gallery,
        flickity: this.flickity.gallery,
      });
    }

    if (this.hasRelatedProducts && !this.flickity.relatedProducts) {
      this._relatedProductsReset();

      this.flickity.relatedProducts = new Flickity(this.relatedProducts, {
        cellAlign: 'left',
        cellSelector: this.selectors.relatedProducts.itemSelector,
        contain: true,
        prevNextButtons: false,
        pageDots: false,
        setGallerySize: false,
      });

      this._flickityEvents({
        el: this.relatedProducts,
        flickity: this.flickity.relatedProducts,
      });
    }
  }

  _destroyFlickity() {
    if (this.flickity.gallery) {
      this.flickity.gallery.destroy();
      this.flickity.gallery = null;

      this.events.unregister(this.onResize);
    }

    if (this.flickity.relatedProducts) {
      this.flickity.relatedProducts.destroy();
      this.flickity.relatedProducts = null;
    }
  }

  _initMasonry() {
    if (this.hasGallery && !this.masonry.gallery) {
      this._galleryReset();
      this.masonry.gallery = new MasonryGrid({
        el: this.gallery,
        settings: {
          itemSelector: this.selectors.gallery.itemSelector,
          columnWidth: this.selectors.gallery.columnWidth,
        },
      });
    }

    if (this.hasRelatedProducts && !this.masonry.relatedProducts) {
      this._relatedProductsReset();

      this.masonry.relatedProducts = new MasonryGrid({
        el: this.relatedProducts,
        settings: {
          itemSelector: this.selectors.relatedProducts.itemSelector,
          columnWidth: this.selectors.relatedProducts.columnWidth,
        },
      });
    }
  }

  _destroyMasonry() {
    if (this.masonry.gallery) {
      this.masonry.gallery.unload();
      this.masonry.gallery = null;
    }

    if (this.masonry.relatedProducts) {
      this.masonry.relatedProducts.unload();
      this.masonry.relatedProducts = null;
    }
  }

  _galleryReset() {
    this.gallery
      .querySelectorAll(this.selectors.gallery.itemSelector)
      .forEach(item => item.setAttribute('style', ''));
  }

  _relatedProductsReset() {
    this.relatedProducts
      .querySelectorAll(this.selectors.relatedProducts.itemSelector)
      .forEach(item => item.setAttribute('style', ''));
  }

  _flickityEvents({ el, flickity }) {
    const event = document.createEvent('Event');
    event.initEvent('product-masonry-layout', true, true);

    this.flickityResizeEvent = () => themeUtils.flickityResize(flickity);
    this.flickityDebouncedEvent = themeUtils.debounce(() => el.dispatchEvent(event), 10);

    flickity.on('cellSelect', () => el.dispatchEvent(event));

    flickity.on('settle', () => el.dispatchEvent(event));

    this.events.register(el, 'product-masonry-layout', this.flickityResizeEvent);

    // Sets the Slider to the height of the first slide
    el.dispatchEvent(event);

    this.onResize = this.events.register(window, 'resize', this.flickityDebouncedEvent);
  }
}
